import React from 'react'
import { Box, Grid, Stack, Typography, Button, Chip, Breadcrumbs, Link, Alert } from '@mui/material'
import { Home as HomeIcon } from '@mui/icons-material'
import VideoCard, { Video } from '../components/VideoCard'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { VideoSuggestion, VideoSeries } from '../types/video'

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

function extractYoutubeId(url: string): string | null {
    try {
        const u = new URL(url)
        if (u.hostname.includes('youtube.com')) {
            const v = u.searchParams.get('v')
            if (v) return v
            const parts = u.pathname.split('/').filter(Boolean)
            const idx = parts.indexOf('embed')
            if (idx >= 0 && parts[idx + 1]) return parts[idx + 1]
        }
        if (u.hostname.includes('youtu.be')) {
            const id = u.pathname.replace('/', '')
            if (id) return id
        }
    } catch { /* ignore */ }
    return null
}

function toVideoModel(s: VideoSuggestion): Video {
    const id = String(s.id)
    const yt = extractYoutubeId(s.youtubeUrl)
    const thumb = yt ? `https://img.youtube.com/vi/${yt}/hqdefault.jpg` : 'https://picsum.photos/seed/fallback/600/338'
    const tags: string[] = (s.tags || '').split(',').map(t => t.trim()).filter(Boolean)
    return {
        id,
        title: s.title,
        thumbnailUrl: thumb,
        duration: '',
        tags,
        series: s.series?.slug
    }
}

const Videos: React.FC = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [items, setItems] = React.useState<Video[]>([])
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState(false)
    const requestId = React.useRef(0)
    const [activeTag, setActiveTag] = React.useState<string>('all')
    const [currentSeries, setCurrentSeries] = React.useState<VideoSeries | null>(null)

    // Get series parameter from URL
    const seriesSlug = searchParams.get('series')

    const load = React.useCallback(async () => {
        const id = ++requestId.current
        setLoading(true)
        setError(false)
        setCurrentSeries(null)
        setItems([])
        setActiveTag('all')
        try {
            let res: Response
            let data: VideoSuggestion[]

            if (seriesSlug) {
                // Load videos for specific series
                res = await fetchWithAuth(buildApiUrl(`/video-suggestions/approved/series/${encodeURIComponent(seriesSlug)}`))
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                data = await res.json()

                // Load series info
                const seriesRes = await fetchWithAuth(buildApiUrl(`/video-series/${encodeURIComponent(seriesSlug)}`))
                if (seriesRes.ok) {
                    const seriesData = await seriesRes.json()
                    if (id === requestId.current) setCurrentSeries(seriesData)
                }
            } else {
                // Load all approved videos
                res = await fetchWithAuth(buildApiUrl('/video-suggestions/approved'))
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                data = await res.json()
            }

            if (id === requestId.current) setItems(data.map(toVideoModel))
        } catch (error) {
            console.error('Failed to load videos:', error)
            if (id === requestId.current) {
                setItems([])
                setError(true)
            }
        } finally {
            if (id === requestId.current) setLoading(false)
        }
    }, [seriesSlug])

    React.useEffect(() => {
        void load()
        return () => { requestId.current += 1 }
    }, [load])

    const allTags = React.useMemo(() => {
        const set = new Set<string>()
        items.forEach(v => v.tags?.forEach(t => set.add(t)))
        return ['all', ...Array.from(set).sort()]
    }, [items])

    const filtered = activeTag === 'all' ? items : items.filter(v => v.tags?.includes(activeTag))

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                    <HomeIcon fontSize="small" />
                    Home
                </Link>
                {currentSeries && (
                    <Typography variant="body2" color="text.primary">
                        {currentSeries.title}
                    </Typography>
                )}
                <Typography variant="body2" color="text.primary">
                    Videos
                </Typography>
            </Breadcrumbs>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 1.5, md: 2 } }}>
                <Box>
                    <Typography variant="h4" fontWeight={800}>
                        {currentSeries ? `${currentSeries.title} Videos` : 'Videos'}
                    </Typography>
                    {currentSeries && currentSeries.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {currentSeries.description}
                        </Typography>
                    )}
                </Box>
                <Button onClick={load} disabled={loading}>Refresh</Button>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mb: { xs: 2, md: 3 }, flexWrap: 'wrap', rowGap: 1 }}>
                {allTags.map(tag => (
                    <Chip key={tag} label={tag} clickable color={activeTag === tag ? 'secondary' : 'default'} variant={activeTag === tag ? 'filled' : 'outlined'} onClick={() => setActiveTag(tag)} />
                ))}
            </Stack>
            {loading && <Typography role="status">Videos werden geladen…</Typography>}
            {error && <Alert severity="error">Videos konnten nicht geladen werden. Bitte erneut versuchen.</Alert>}
            <Grid container spacing={{ xs: 2, md: 3 }}>
                {filtered.map(v => (
                    <Grid key={v.id} item xs={12} sm={6} md={3}>
                        <VideoCard video={v} onClick={(id) => navigate(`/video/${id}`)} />
                    </Grid>
                ))}
                {!loading && !error && filtered.length === 0 && (
                    <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', opacity: 0.8, py: 6 }}>
                            <Typography variant="body1">Noch keine freigegebenen Videos.</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}

export default Videos